import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  EyeSlashIcon,
  LanguageIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { blogPostRecordQuery } from "@/app/Blogs/GraphQL/blogPostRecordQuery";
import {
  deleteBlogPostMutation,
  translateBlogPostMutation,
  updateBlogPostMutation,
} from "@/app/Blogs/GraphQL/blogPostMutations";
import { blogPostRoute } from "@/app/Blogs/Routes/blogRoutes";
import { Badge } from "@/components/catalyst/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ActionGroupButton } from "@/lib/Components/Actions/ActionGroupButtons";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { toast } from "sonner";
import { format } from "date-fns";
import { useNavigate } from "@tanstack/react-router";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";

export function BlogPostRecordScreen() {
  const { id } = blogPostRoute.useParams();
  const { isAdmin } = useTenant();
  const { data } = useGqlQuery(blogPostRecordQuery, { id });
  const { mutateAsync: updateBlogPost } = useGqlMutation(
    updateBlogPostMutation,
  );
  const { mutateAsync: translateBlogPost } = useGqlMutation(
    translateBlogPostMutation,
  );
  const { mutateAsync: deleteBlogPost } = useGqlMutation(
    deleteBlogPostMutation,
  );
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const navigate = useNavigate();

  const post = data?.blogPost;

  if (!post) {
    return <div className="p-6 text-zinc-500">Post not found.</div>;
  }

  const appUrl = import.meta.env.VITE_APP_URL as string | undefined;
  const buildPublicUrl = (locale: string, postSlug: string) =>
    appUrl ? `${appUrl}/${locale}/blog/${postSlug}` : null;
  const sourceUrl = buildPublicUrl(post.locale, post.slug);

  const actions: ActionGroupButton[] = [
    {
      label: "Actions",
      items: [
        {
          actions: [
            {
              label: "View",
              Icon: ArrowTopRightOnSquareIcon,
              isHidden: !sourceUrl,
              handleAction: () => {
                if (sourceUrl) {
                  window.open(sourceUrl, "_blank", "noopener,noreferrer");
                }
              },
            },
            {
              label: "Publish",
              Icon: CheckCircleIcon,
              color: "green",
              isHidden: post.is_published,
              handleAction: async () => {
                await updateBlogPost({
                  id: post.id,
                  input: {
                    published_at: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
                  },
                });
                toast.success("Blog post published");
              },
            },
            {
              label: "Unpublish",
              Icon: EyeSlashIcon,
              isHidden: !post.is_published,
              handleAction: async () => {
                await updateBlogPost({
                  id: post.id,
                  input: { published_at: null },
                });
                toast.success("Blog post unpublished");
              },
            },
            {
              label: "Translate",
              Icon: LanguageIcon,
              handleAction: async () => {
                await translateBlogPost({
                  id: post.id,
                  locale: null,
                });
                toast.success(
                  "Translation started — locales will appear once processed",
                );
              },
            },
          ],
        },
        {
          actions: [
            {
              label: "Delete",
              Icon: TrashIcon,
              color: "red",
              isHidden: !isAdmin,
              handleAction: () =>
                openConfirmDelete({
                  title: "Delete blog post",
                  message: `Delete "${post.title}"? Its translations, tags and reactions are removed too. This cannot be undone.`,
                  onDelete: async () => {
                    await deleteBlogPost({ id: post.id });
                    toast.success("Blog post deleted");
                    await navigate({
                      from: "/$tenant/$orgId/supplier/{-$supplierId}",
                      to: "/$tenant/$orgId/supplier/{-$supplierId}/blogs",
                      params: (prev) => prev,
                      search: (prev) => prev,
                    });
                  },
                }),
            },
          ],
        },
      ],
    },
  ];

  const translationLinks = post.translations
    .map((t) => {
      const url = buildPublicUrl(t.locale, post.slug);
      return url ? { locale: t.locale, url } : null;
    })
    .filter(
      (entry): entry is { locale: string; url: string } => entry !== null,
    );

  return (
    <RecordScreen
      tabParams={{ id }}
      className="@container"
      title={post.title}
      actionsGroups={actions}
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/blogs"
                params={(prev) => prev}
              >
                Blogs
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{post.slug}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      badges={
        <>
          <Badge color={post.is_published ? "green" : "zinc"}>
            {post.is_published ? "Published" : "Draft"}
          </Badge>
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 hover:bg-zinc-200"
            >
              {post.locale}
            </a>
          ) : (
            <Badge color="zinc">{post.locale}</Badge>
          )}
          {translationLinks.map((link) => (
            <a
              key={link.locale}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              {link.locale}
              <ArrowTopRightOnSquareIcon className="size-3" />
            </a>
          ))}
        </>
      }
      tabs={[
        {
          name: "Details",
          Icon: DocumentTextIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/blogs/$id",
          params: (prev) => ({ ...prev, id: post.id }),
        },
        {
          name: "Translations",
          Icon: LanguageIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/blogs/$id/translations",
          params: (prev) => ({ ...prev, id: post.id }),
        },
      ]}
    />
  );
}
